package com.bulut.mecitogullari.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.KeyboardArrowLeft
import androidx.compose.material.icons.filled.KeyboardArrowRight
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.localizedCategory
import com.bulut.mecitogullari.core.util.YearMonth
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.core.util.formatDateShort
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.ui.theme.AppColors

/** Money with large, readable typography. */
@Composable
fun MoneyText(
    amount: Double,
    modifier: Modifier = Modifier,
    color: Color = MaterialTheme.colorScheme.onSurface,
    large: Boolean = false,
) {
    Text(
        text = formatAmount(amount),
        style = if (large) MaterialTheme.typography.displaySmall else MaterialTheme.typography.titleLarge,
        color = color,
        modifier = modifier,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
}

/** Small summary card used on the dashboard and reports. */
@Composable
fun StatCard(
    title: String,
    amount: Double,
    modifier: Modifier = Modifier,
    amountColor: Color = MaterialTheme.colorScheme.onSurface,
    container: Color = MaterialTheme.colorScheme.surface,
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = container),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Text(
                text = title,
                style = MaterialTheme.typography.labelMedium,
                color = AppColors.TextSecondary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = formatAmount(amount),
                style = MaterialTheme.typography.titleMedium,
                color = amountColor,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.padding(top = 6.dp),
            )
        }
    }
}

@Composable
fun SectionTitle(text: String, modifier: Modifier = Modifier) {
    Text(
        text = text,
        style = MaterialTheme.typography.titleMedium,
        color = AppColors.TextPrimary,
        modifier = modifier,
    )
}

@Composable
fun EmptyState(message: String, modifier: Modifier = Modifier) {
    Box(modifier = modifier.fillMaxWidth().padding(vertical = 32.dp), contentAlignment = Alignment.Center) {
        Text(
            text = message,
            style = MaterialTheme.typography.bodyMedium,
            color = AppColors.TextSecondary,
            textAlign = TextAlign.Center,
        )
    }
}

/** One ledger row: colored +/- badge, description, category • date, amount. */
@Composable
fun TransactionRow(
    transaction: CashTransaction,
    modifier: Modifier = Modifier,
    projectName: String? = null,
    onClick: (() -> Unit)? = null,
) {
    val strings = LocalStrings.current
    val isIncome = transaction.type == TransactionType.INCOME
    val accent = if (isIncome) AppColors.Income else AppColors.Expense
    val accentContainer = if (isIncome) AppColors.IncomeContainer else AppColors.ExpenseContainer

    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = modifier.fillMaxWidth().let { m -> if (onClick != null) m.clickable { onClick() } else m },
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.size(38.dp).background(accentContainer, CircleShape),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = if (isIncome) "+" else "−",
                    color = accent,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                )
            }
            Column(modifier = Modifier.weight(1f).padding(horizontal = 12.dp)) {
                Text(
                    text = transaction.description.ifBlank {
                        transaction.category
                            .ifBlank { if (isIncome) strings.income else strings.expense }
                            .let(::localizedCategory)
                    },
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                val subtitle = buildString {
                    append(formatDateShort(transaction.dateEpochMillis))
                    if (transaction.category.isNotBlank()) {
                        append(" • ${localizedCategory(transaction.category)}")
                    }
                    if (projectName != null) append(" • $projectName")
                    if (!transaction.inDailyLedger) append(" • ${strings.projectOnlyBadge}")
                }
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.padding(top = 2.dp),
                )
            }
            Text(
                text = (if (isIncome) "+" else "−") + formatAmount(transaction.amount),
                style = MaterialTheme.typography.titleSmall,
                color = accent,
                maxLines = 1,
            )
        }
    }
}

/** ‹ Temmuz 2026 › month stepper. */
@Composable
fun MonthSelector(
    month: YearMonth,
    onPrevious: () -> Unit,
    onNext: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(onClick = onPrevious) {
            Icon(Icons.Default.KeyboardArrowLeft, contentDescription = LocalStrings.current.previousMonth)
        }
        Text(
            text = month.label,
            style = MaterialTheme.typography.titleMedium,
        )
        IconButton(onClick = onNext) {
            Icon(Icons.Default.KeyboardArrowRight, contentDescription = LocalStrings.current.nextMonth)
        }
    }
}

